import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import { get, debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { FaInfoCircle, FaPencilAlt, FaPlus, FaTimes } from 'react-icons/fa';

import { formatDate } from 'App/components/PatientProfile/PatientProfile.utils';
import useFetchPatientMedicationRxNumber from 'App/hooks/useFetchPatientMedicationRxNumber';
import getFeatureFlagStatus from 'App/utils/getFeatureFlagStatus';
import { PERMISSION_TYPES } from 'App/utils/isPermitted';
import useFetchDispensedProductAutocomplete from '../../hooks/useFetchDispensedProductAutocomplete';
import { DatePickerField } from 'Shared/form/DatePickerField';
import { NumberField } from 'Shared/form/NumberField';
import { TextField } from 'Shared/form/TextField';
import SelectField from 'Shared/form/SelectField';
import CommonPatientFieldWrapper from '../CommonPatientFieldWrapper';
import PhysicianTypeaheadField from './PhysicianTypeaheadField';
import RxFillForm from './RxFillForm';
import RxNumberEndModal from './RxNumberEndModal';

const RxBorderContainer = styled.div`
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 15px;
    margin-bottom: 5px;
`;

const ButtonContainer = styled.div`
    visibility: hidden;
`;

const RxNumberFormAreaContainer = styled.div`
    display: flex;

    &:hover ${ButtonContainer} {
        visibility: visible;
    }
`;

const formatDispensedProductOptionLabel = (option, { context }) => (
    <div
        style={{
            paddingBottom: context === 'menu' ? 4 : 0,
            fontSize: context === 'value' ? '0.9em' : undefined,
        }}
    >
        <div>{option.productDescription || option.label}</div>
        {context === 'menu' && <div style={{ fontSize: '0.85em', color: '#888' }}>({option.ndc || option.value})</div>}
    </div>
);

const RxNumberForm = ({ fieldPrefix, hasMedicationGPI10Mapping, onClickRemove }) => {
    const { values, initialValues, setFieldValue } = useFormikContext();
    const rxValues = get(values, fieldPrefix);
    const initialRxValues = get(initialValues, fieldPrefix);
    const rxFills = rxValues.rxFill;
    const [endModalOpen, setEndModalOpen] = useState(false);
    const isCodifiedMedicationsEnabled = getFeatureFlagStatus(PERMISSION_TYPES.ENABLE_CODIFY_MEDICATIONS);
    const [dispensedProductInput, setDispensedProductInput] = useState('');
    const medicationId = values.patientMedication?.medication?.ID || values.patientMedication?.medication?.id || '';

    const { data: dispensedProducts, isLoading: isDispensedProductLoading } = useFetchDispensedProductAutocomplete({
        inputValue: isCodifiedMedicationsEnabled ? dispensedProductInput : '',
        medicationId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedDispensedProductChange = useCallback(
        // eslint-disable-next-line react-hooks/use-memo
        debounce((newValue) => setDispensedProductInput(newValue), 500),
        []
    );

    useEffect(() => {
        if (rxValues.dispensedMedication && !rxValues.dispensedProductDescription) {
            setFieldValue(`${fieldPrefix}.dispensedProductDescription`, {
                productDescription: rxValues.dispensedMedication,
                ndc: rxValues.ndc || '',
                value: rxValues.ndc || '',
                label: rxValues.dispensedMedication,
            });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const { data: patientMedRx } = useFetchPatientMedicationRxNumber(
        values.patientId,
        values.patientMedication.medicationId
    );
    const savedRxValues = patientMedRx?.find((rx) => rx.id === rxValues.id);

    return (
        <RxBorderContainer>
            <RxNumberFormAreaContainer>
                <div style={{ flexGrow: 1 }}>
                    {isCodifiedMedicationsEnabled && (
                        <Row>
                            <CommonPatientFieldWrapper fieldName="dispensedMedication">
                                {(commonProps) => (
                                    <>
                                        <Col md={4}>
                                            <SelectField
                                                fieldName={`${fieldPrefix}.dispensedProductDescription`}
                                                label={
                                                    <span>
                                                        Product Description
                                                        {!hasMedicationGPI10Mapping && (
                                                            <OverlayTrigger
                                                                trigger={['hover', 'focus']}
                                                                placement="top"
                                                                overlay={
                                                                    <Tooltip id="dispensed-product-tooltip">
                                                                        This information is not available for the
                                                                        selected medication. If you believe this is
                                                                        incorrect, please submit a support ticket.
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <FaInfoCircle className="fa-fw" />
                                                            </OverlayTrigger>
                                                        )}
                                                    </span>
                                                }
                                                placeholder={
                                                    hasMedicationGPI10Mapping
                                                        ? 'Search product...'
                                                        : 'Product description not available'
                                                }
                                                isDisabled={
                                                    commonProps.disabled ||
                                                    !hasMedicationGPI10Mapping ||
                                                    (!!rxValues.id && rxValues.dispensedProductDescription)
                                                }
                                                required={hasMedicationGPI10Mapping && commonProps.required}
                                                options={
                                                    dispensedProducts?.results
                                                        ? dispensedProducts.results.map((product) => ({
                                                              ...product,
                                                              value: product.ndc,
                                                              label: product.productDescription,
                                                          }))
                                                        : []
                                                }
                                                filterOptionType="all"
                                                formatOptionLabel={formatDispensedProductOptionLabel}
                                                getOptionValue={(option) => option.ndc || option.value}
                                                onInputChange={debouncedDispensedProductChange}
                                                isLoading={isDispensedProductLoading}
                                                isClearable
                                                components={{
                                                    DropdownIndicator: () => null,
                                                    IndicatorSeparator: () => null,
                                                }}
                                                onChange={(selected) => {
                                                    setFieldValue(
                                                        `${fieldPrefix}.dispensedProductDescription`,
                                                        selected
                                                    );
                                                    setFieldValue(
                                                        `${fieldPrefix}.dispensedMedication`,
                                                        selected?.productDescription || selected?.label || ''
                                                    );
                                                    setFieldValue(`${fieldPrefix}.ndc`, selected?.ndc || '');
                                                }}
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <TextField
                                                fieldName={`${fieldPrefix}.ndc`}
                                                label="Product NDC"
                                                placeholder={
                                                    hasMedicationGPI10Mapping ? 'Product NDC' : 'NDC not available'
                                                }
                                                required={hasMedicationGPI10Mapping && commonProps.required}
                                                disabled
                                            />
                                        </Col>
                                    </>
                                )}
                            </CommonPatientFieldWrapper>
                        </Row>
                    )}
                    <Row>
                        <CommonPatientFieldWrapper fieldName="rxNumber">
                            {(commonProps) => (
                                <Col md={2}>
                                    <TextField
                                        {...commonProps}
                                        fieldName={`${fieldPrefix}.rxNumber`}
                                        label="Rx Number"
                                        placeholder="Rx Number"
                                        disabled={commonProps.disabled || !!savedRxValues?.rxNumber}
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                        <CommonPatientFieldWrapper fieldName="rxStrength">
                            {(commonProps) => (
                                <Col md={2}>
                                    <TextField
                                        {...commonProps}
                                        fieldName={`${fieldPrefix}.strength`}
                                        label="Rx Strength"
                                        placeholder="Rx Strength"
                                        disabled={commonProps.disabled || !!savedRxValues?.strength}
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                        <CommonPatientFieldWrapper fieldName="refillsAllowed">
                            {(commonProps) => (
                                <Col md={2}>
                                    <NumberField
                                        {...commonProps}
                                        fieldName={`${fieldPrefix}.refillsAuthorized`}
                                        label="Refills Allowed"
                                        placeholder="Refills Allowed"
                                        min={0}
                                        max={99}
                                        disabled={commonProps.disabled || savedRxValues?.refillsAuthorized > 0}
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                        <CommonPatientFieldWrapper fieldName="rxPrescriber">
                            {(commonProps) => (
                                <Col md={3}>
                                    <PhysicianTypeaheadField
                                        {...commonProps}
                                        fieldPrefix={fieldPrefix}
                                        disabled={
                                            commonProps.disabled ||
                                            !!savedRxValues?.physician ||
                                            (!!savedRxValues && !!initialRxValues?.physician)
                                        }
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                        {!rxValues.end && rxValues.id && (
                            <Col md={3} style={{ marginTop: 25 }}>
                                <Button variant="outline-secondary" block onClick={() => setEndModalOpen(true)}>
                                    End Rx Number
                                </Button>
                            </Col>
                        )}
                    </Row>
                    <Row>
                        <CommonPatientFieldWrapper fieldName="rxNumberWrittenDate">
                            {(commonProps) => (
                                <Col md={3}>
                                    <DatePickerField
                                        {...commonProps}
                                        fieldName={`${fieldPrefix}.start`}
                                        label="Rx Number Written Date"
                                        maxDate={dayjs().toDate()}
                                        disabled={commonProps.disabled || !!savedRxValues?.start}
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                        <CommonPatientFieldWrapper fieldName="rxNumberExpirationDate">
                            {(commonProps) => (
                                <Col md={3}>
                                    <DatePickerField
                                        {...commonProps}
                                        fieldName={`${fieldPrefix}.fillExpiration`}
                                        label="Rx Number Exp. Date"
                                        disabled={commonProps.disabled || !!savedRxValues?.fillExpiration}
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                        {isCodifiedMedicationsEnabled && (
                            <CommonPatientFieldWrapper fieldName="dispensedPharmacy">
                                {(commonProps) => (
                                    <Col md={3}>
                                        <TextField
                                            {...commonProps}
                                            fieldName={`${fieldPrefix}.dispensedPharmacy`}
                                            label="Dispensed Pharmacy"
                                            placeholder="Dispensed Pharmacy"
                                            disabled={commonProps.disabled || !!savedRxValues?.dispensedPharmacy}
                                        />
                                    </Col>
                                )}
                            </CommonPatientFieldWrapper>
                        )}
                    </Row>
                    {rxValues.end && (
                        <div className="global-info-message">
                            <FaInfoCircle className="fa-fw" />
                            <span>
                                Ending prescription because of {rxValues.endReason}, on {formatDate(rxValues.end)}
                            </span>
                            {!savedRxValues?.end && (
                                <>
                                    <Button variant="link" onClick={() => setEndModalOpen(true)}>
                                        <FaPencilAlt /> Edit
                                    </Button>
                                    <Button
                                        variant="link"
                                        onClick={() => {
                                            setFieldValue(`${fieldPrefix}.end`, null);
                                            setFieldValue(`${fieldPrefix}.endReason`, null);
                                            setFieldValue(`${fieldPrefix}.notes`, null);
                                        }}
                                    >
                                        <FaTimes /> Cancel Prescription End
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                </div>

                <ButtonContainer>
                    <Button
                        variant="link"
                        style={{ visibility: get(values, `${fieldPrefix}.id`) ? 'hidden' : undefined }}
                        onClick={onClickRemove}
                        aria-label="Remove Rx Number"
                    >
                        <FaTimes />
                    </Button>
                </ButtonContainer>
            </RxNumberFormAreaContainer>

            {rxFills?.length > 0 && (
                <RxBorderContainer>
                    {rxFills.map((rxFill, index) => (
                        <RxFillForm
                            key={rxFill.id || `index-${index}`}
                            fieldPrefix={`${fieldPrefix}.rxFill.[${index}]`}
                            onClickRemove={() => {
                                rxFills.splice(index, 1);
                                setFieldValue(`${fieldPrefix}.rxFill`, rxFills);
                            }}
                        />
                    ))}
                </RxBorderContainer>
            )}

            <Button
                variant="link"
                disabled={(rxValues.refillsAuthorized || 0) < rxFills?.length}
                onClick={() => {
                    const newIndex = rxFills?.length || 0;
                    setFieldValue(`${fieldPrefix}.rxFill.[${newIndex}]`, {});
                }}
            >
                <FaPlus /> Add Fill
            </Button>

            {endModalOpen && (
                <RxNumberEndModal
                    existingRxNumber={rxValues}
                    onClose={() => setEndModalOpen(false)}
                    onSubmit={(modalValues) => {
                        setFieldValue(fieldPrefix, modalValues.rxNumber);
                    }}
                />
            )}
        </RxBorderContainer>
    );
};

RxNumberForm.propTypes = {
    fieldPrefix: PropTypes.string.isRequired,
    hasMedicationGPI10Mapping: PropTypes.bool,
    onClickRemove: PropTypes.func.isRequired,
};

RxNumberForm.defaultProps = {
    hasMedicationGPI10Mapping: false,
};

export default RxNumberForm;
